import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Chip,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    Restaurant as RestaurantIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Search as SearchIcon,
    RestartAlt as RestartIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../config/api';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const TrofologiaCombinaciones = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [validationResult, setValidationResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(false);
    const [error, setError] = useState(null);

    // Fetch categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                const response = await fetch(`${API_BASE_URL}/trophology/categories`, {
                    headers: headers
                });

                if (!response.ok) throw new Error('Error al cargar categorías');

                const data = await response.json();
                setCategories(data);
            } catch (err) {
                console.error('Error fetching categories:', err);
                setError('No se pudieron cargar las categorías de alimentos.');
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleToggleCategory = (categoryId) => {
        setSelectedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                if (prev.length >= 3) {
                    // Optional: Limit selection to 3 for simplicity, or allow more
                    return prev;
                }
                return [...prev, categoryId];
            }
        });
        // Reset validation when selection changes
        setValidationResult(null);
    };

    const handleValidate = async () => {
        if (selectedCategories.length < 2) return;

        setValidating(true);
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            };

            const response = await fetch(`${API_BASE_URL}/trophology/validate`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ category_ids: selectedCategories })
            });

            if (!response.ok) throw new Error('Error en validación');

            const data = await response.json();
            setValidationResult(data);
        } catch (err) {
            console.error('Error validating:', err);
            setError('Error al validar la combinación.');
        } finally {
            setValidating(false);
        }
    };

    const handleReset = () => {
        setSelectedCategories([]);
        setValidationResult(null);
    };

    const getCategoryName = (id) => {
        const cat = categories.find(c => c.id === id);
        return cat ? cat.name : 'Desconocido';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <MotionPaper
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{
                    p: 4,
                    mb: 4,
                    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                    color: 'white',
                    borderRadius: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <RestaurantIcon sx={{ fontSize: 40 }} />
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Trofología Digital
                        </Typography>
                        <Typography variant="subtitle1">
                            Validador de combinaciones según Manuel Lezaeta Acharán
                        </Typography>
                    </Box>
                </Box>
            </MotionPaper>

            <Grid container spacing={4}>
                {/* Selector de Categorías */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom fontWeight={700}>
                                1. Selecciona tus alimentos
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Elige de 2 a 3 categorías para validar su compatibilidad digestiva.
                            </Typography>

                            <Grid container spacing={1}>
                                {categories.map((cat) => {
                                    const isSelected = selectedCategories.includes(cat.id);
                                    return (
                                        <Grid item xs={6} sm={4} key={cat.id}>
                                            <Paper
                                                onClick={() => handleToggleCategory(cat.id)}
                                                elevation={isSelected ? 4 : 1}
                                                sx={{
                                                    p: 2,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected ? 'primary.light' : 'background.paper',
                                                    color: isSelected ? 'white' : 'text.primary',
                                                    transition: 'all 0.2s',
                                                    border: isSelected ? '2px solid #2E7D32' : '1px solid #eee',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 3
                                                    }
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={600}>
                                                    {cat.name}
                                                </Typography>
                                                {cat.examples && (
                                                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem', mt: 0.5 }}>
                                                        {cat.examples.split(',').slice(0, 2).join(', ')}...
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={selectedCategories.length < 2 || validating}
                                    onClick={handleValidate}
                                    startIcon={validating ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)'
                                    }}
                                >
                                    {validating ? 'Analizando...' : 'Validar Combinación'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleReset}
                                    disabled={selectedCategories.length === 0}
                                >
                                    <RestartIcon />
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Resultados */}
                <Grid item xs={12} md={6}>
                    <AnimatePresence mode="wait">
                        {validationResult ? (
                            <MotionCard
                                key="result"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                sx={{ height: '100%', borderRadius: 3, bgcolor: validationResult.valid ? '#f1f8e9' : '#fff3e0' }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        {validationResult.valid ? (
                                            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                                        ) : (
                                            <WarningIcon color="error" sx={{ fontSize: 40 }} />
                                        )}
                                        <Box>
                                            <Typography variant="h5" fontWeight={700} color={validationResult.valid ? 'success.main' : 'error.main'}>
                                                {validationResult.valid ? 'Combinación Saludable' : 'Combinación No Recomendada'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Según principios de Trofología
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Issues (Incompatibilidades) */}
                                    {validationResult.issues.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                                Conflictos Detectados:
                                            </Typography>
                                            {validationResult.issues.map((issue, index) => (
                                                <Alert severity="error" key={index} sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {issue.category1} + {issue.category2}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {issue.reason}
                                                    </Typography>
                                                    {issue.page_reference && (
                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                            Ref: {issue.page_reference}
                                                        </Typography>
                                                    )}
                                                </Alert>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Sugerencias */}
                                    {validationResult.suggestions.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                                Sugerencias del Experto:
                                            </Typography>
                                            <List dense>
                                                {validationResult.suggestions.map((sugg, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <InfoIcon color="info" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={sugg} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {/* Principio de Lezaeta */}
                                    {validationResult.lezaeta_principle && (
                                        <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderLeft: '4px solid #2E7D32' }}>
                                            <Typography variant="body2" fontStyle="italic">
                                                "{validationResult.lezaeta_principle}"
                                            </Typography>
                                            <Typography variant="caption" display="block" textAlign="right" sx={{ mt: 1 }}>
                                                — Manuel Lezaeta Acharán
                                            </Typography>
                                        </Paper>
                                    )}

                                </CardContent>
                            </MotionCard>
                        ) : (
                            <Card sx={{ height: '100%', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                                <Box sx={{ textAlign: 'center', p: 3, opacity: 0.6 }}>
                                    <SearchIcon sx={{ fontSize: 60, mb: 2 }} />
                                    <Typography variant="h6">
                                        Esperando selección...
                                    </Typography>
                                    <Typography variant="body2">
                                        Selecciona categorías y presiona Validar para ver el análisis trofológico.
                                    </Typography>
                                </Box>
                            </Card>
                        )}
                    </AnimatePresence>
                </Grid>
            </Grid>
        </Container>
    );
};

export default TrofologiaCombinaciones;
